/* ============================================================
   Sticky Add to Cart — Haven Tents

   Mirrors the static add-to-cart button in the product form and
   proxies clicks straight to it, so variant, quantity, upsells,
   gift options and the cart drawer all behave identically.

   Visibility: shown only once the static button has scrolled out
   of view *above* the viewport, hidden again as soon as it is
   back on screen (or while a drawer/modal is open).
   ============================================================ */

if (!customElements.get("sticky-add-to-cart")) {
  customElements.define(
    "sticky-add-to-cart",
    class StickyAddToCart extends HTMLElement {
      connectedCallback() {
        // The bar renders next to the buy buttons, which sit inside
        // .product__info-wrapper — and with reveal-on-scroll animations on, that
        // wrapper carries a transform, which would make our position:fixed
        // resolve against it instead of the viewport. Reparenting to <body>
        // makes the bar immune to whatever markup ends up around it.
        // (appendChild re-enters this callback, hence the initialized guard.)
        if (this.parentElement !== document.body) {
          document.body.appendChild(this);
          return;
        }

        // One bar per page, whichever section rendered it first.
        const existing = document.querySelector(
          "sticky-add-to-cart.sticky-atc--ready",
        );
        if (existing && existing !== this) {
          this.remove();
          return;
        }

        if (this.initialized) return;
        this.initialized = true;
        this.classList.add("sticky-atc--ready");

        this.sectionId = this.dataset.sectionId;
        this.productFormId = this.dataset.productFormId;

        this.button = this.querySelector("[data-sticky-atc-button]");
        this.label = this.querySelector("[data-sticky-atc-label]");
        this.priceTarget = this.querySelector("[data-sticky-atc-price]");
        this.variantLabel = this.querySelector("[data-sticky-atc-variant]");
        this.variantText = this.querySelector("[data-sticky-atc-variant-text]");
        this.spinner = this.querySelector(".loading-overlay__spinner");

        if (!this.button) return;

        this.blocked = false;
        this.pastAnchor = false;
        this.adjusted = new Map();

        this.button.addEventListener("click", this.onClick.bind(this));

        // Chat/quiz widgets are injected at runtime by their apps, so there are
        // no selectors to key off — find whatever is pinned to the bottom of the
        // viewport instead, and re-scan when a widget mounts late.
        this.widgetObserver = new MutationObserver(() => {
          if (this.shown) this.scheduleWidgetAvoidance();
        });
        this.widgetObserver.observe(document.body, {
          childList: true,
          subtree: true,
        });

        this.syncHandler = this.scheduleSync.bind(this);
        this.mutationObserver = new MutationObserver(this.syncHandler);
        this.blockObserver = new MutationObserver(
          this.updateBlockedState.bind(this),
        );

        // A rect check on every animation frame while scrolling, rather than an
        // IntersectionObserver: instant jumps (anchor links, scroll restoration,
        // "back to top") can move the anchor from above the viewport to below it
        // without ever crossing an intersection threshold, which would strand the
        // bar on screen.
        this.positionHandler = this.schedulePositionCheck.bind(this);
        window.addEventListener("scroll", this.positionHandler, {
          passive: true,
        });
        window.addEventListener("resize", this.positionHandler, {
          passive: true,
        });

        // Whether a widget clashes with the bar depends on how wide the window
        // is, so a resize can flip a widget from "leave it alone" to "move it"
        // and back. Release everything and re-decide from scratch: the two runs
        // happen in the same callback, so nothing paints in between.
        this.avoidanceResizeHandler = () => {
          if (!this.shown) return;
          this.releaseWidgets();
          this.updateWidgetAvoidance();
        };
        window.addEventListener("resize", this.avoidanceResizeHandler, {
          passive: true,
        });

        // Re-measure the variant marquee on resize (rotation, dev tools,
        // window drag) — the space available for the text can change even
        // though the text itself hasn't.
        if (this.variantText) {
          this.marqueeResizeHandler = () => this.updateVariantMarquee();
          window.addEventListener("resize", this.marqueeResizeHandler, {
            passive: true,
          });
        }

        // Media loading in above the anchor moves it without a scroll event.
        if (window.ResizeObserver) {
          this.resizeObserver = new ResizeObserver(this.positionHandler);
          this.resizeObserver.observe(document.documentElement);
        }

        this.observeProductInfo();
        this.observeOverlays();
        this.sync();
        this.updateBlockedState();
        this.checkPosition();
      }

      disconnectedCallback() {
        if (!this.initialized) return;
        if (this.mutationObserver) this.mutationObserver.disconnect();
        if (this.blockObserver) this.blockObserver.disconnect();
        if (this.widgetObserver) this.widgetObserver.disconnect();
        if (this.resizeObserver) this.resizeObserver.disconnect();
        this.shown = false;
        this.updateWidgetAvoidance();
        this.toggleWidgetPolling(false);
        if (this.avoidanceFrame) cancelAnimationFrame(this.avoidanceFrame);
        window.removeEventListener("scroll", this.positionHandler);
        window.removeEventListener("resize", this.positionHandler);
        if (this.avoidanceResizeHandler) {
          window.removeEventListener("resize", this.avoidanceResizeHandler);
        }
        if (this.marqueeResizeHandler) {
          window.removeEventListener("resize", this.marqueeResizeHandler);
        }
        if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
        if (this.positionFrame) cancelAnimationFrame(this.positionFrame);
        this.classList.remove("sticky-atc--ready");
        this.initialized = false;
      }

      /* ── Source elements (re-queried; the theme swaps them on variant change) ── */

      get sourceButton() {
        return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      }

      get sourcePrice() {
        return document.getElementById(`price-${this.sectionId}`);
      }

      // The full variants array, embedded as JSON by the variant picker — only
      // present when the product has real options (has_only_default_variant
      // is false). Cached: the list of variants itself never changes.
      get variantsData() {
        if (this._variantsData !== undefined) return this._variantsData;
        const picker =
          document.getElementById(`variant-radios-${this.sectionId}`) ||
          document.getElementById(`variant-selects-${this.sectionId}`);
        const script = picker && picker.querySelector('[type="application/json"]');
        try {
          this._variantsData = script ? JSON.parse(script.textContent) : null;
        } catch (e) {
          this._variantsData = null;
        }
        return this._variantsData;
      }

      get anchor() {
        const scope =
          document.getElementById(this.productFormId) ||
          document.getElementById(`MainProduct-${this.sectionId}`);
        if (!scope) return this.sourceButton;
        return (
          scope.querySelector(".qty-and-add-to-cart__container") ||
          scope.querySelector(".product-form__buttons") ||
          this.sourceButton
        );
      }

      /* ── Observation ── */

      observeProductInfo() {
        const productInfo = document.getElementById(
          `ProductInfo-${this.sectionId}`,
        );
        if (!productInfo) return;
        this.mutationObserver.observe(productInfo, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ["class", "disabled", "aria-disabled"],
        });
      }

      observeOverlays() {
        this.blockObserver.observe(document.body, {
          attributes: true,
          attributeFilter: ["class"],
        });

        document
          .querySelectorAll("cart-drawer, .mnav-drawer, #menu-drawer")
          .forEach((el) => {
            this.blockObserver.observe(el, {
              attributes: true,
              attributeFilter: ["class", "open"],
            });
          });
      }

      schedulePositionCheck() {
        if (this.positionFrame) return;
        this.positionFrame = requestAnimationFrame(() => {
          this.positionFrame = null;
          this.checkPosition();
        });
      }

      checkPosition() {
        const anchor = this.anchor;
        // "Past" only once the static button has left the top of the viewport —
        // never while it is still below the fold.
        const pastAnchor = anchor
          ? anchor.getBoundingClientRect().bottom <= 0
          : false;
        if (pastAnchor === this.pastAnchor) return;
        this.pastAnchor = pastAnchor;
        this.render();
      }

      /* ── Blocked while a drawer / modal owns the screen ── */

      updateBlockedState() {
        const blocked =
          document.body.classList.contains("overflow-hidden") ||
          document.body.classList.contains("overflow-hidden-mobile") ||
          document.body.classList.contains("overflow-hidden-tablet") ||
          !!document.querySelector("cart-drawer.active") ||
          !!document.querySelector(".mnav-drawer.is-open");

        if (blocked === this.blocked) return;
        this.blocked = blocked;
        this.render();
      }

      /* ── Mirror label / disabled / loading / price ── */

      scheduleSync() {
        if (this.syncFrame) return;
        this.syncFrame = requestAnimationFrame(() => {
          this.syncFrame = null;
          this.sync();
          this.checkPosition();
        });
      }

      sync() {
        const source = this.sourceButton;

        if (!source) {
          this.button.disabled = true;
          this.available = false;
          this.render();
          return;
        }

        const sourceLabel = source.querySelector("span");
        if (sourceLabel && this.label) {
          const text = sourceLabel.textContent.trim();
          if (text && this.label.textContent.trim() !== text) {
            this.label.textContent = text;
          }
        }

        const loading = source.classList.contains("loading");
        const disabled = source.hasAttribute("disabled");
        const busy = source.getAttribute("aria-disabled") === "true";

        this.button.disabled = disabled;
        this.button.setAttribute("aria-disabled", busy ? "true" : "false");
        this.button.classList.toggle("loading", loading);
        if (this.spinner) this.spinner.classList.toggle("hidden", !loading);

        this.available = true;
        this.syncPrice();
        this.syncVariantLabel();
        this.render();
      }

      syncVariantLabel() {
        if (!this.variantText) return;
        const variants = this.variantsData;
        if (!variants) return;

        const form = document.getElementById(this.productFormId);
        const idInput = form && form.querySelector('[name="id"]');
        const id = idInput && Number(idInput.value);
        const variant = variants.find((v) => v.id === id);
        if (variant && this.variantText.textContent.trim() !== variant.title) {
          this.variantText.textContent = variant.title;
        }
        this.updateVariantMarquee();
      }

      // Only animates when the title is actually too long for the space it's
      // given — most variant names fit and just sit still. Speed is constant
      // (~22px/s) regardless of how much text overflows, so a barely-clipped
      // title and a long one both read at the same comfortable pace.
      updateVariantMarquee() {
        if (!this.variantLabel || !this.variantText) return;
        const overflow = Math.round(
          this.variantText.scrollWidth - this.variantLabel.clientWidth,
        );

        if (overflow <= 2) {
          this.variantText.classList.remove("sticky-atc__variant-text--marquee");
          this.variantText.style.removeProperty("--marquee-distance");
          this.variantText.style.removeProperty("--marquee-duration");
          return;
        }

        const pxPerSecond = 22;
        const travelTime = overflow / pxPerSecond;
        const duration = Math.max(6, (travelTime * 2) / 0.7);
        this.variantText.style.setProperty(
          "--marquee-distance",
          `-${overflow}px`,
        );
        this.variantText.style.setProperty(
          "--marquee-duration",
          `${duration.toFixed(1)}s`,
        );
        this.variantText.classList.add("sticky-atc__variant-text--marquee");
      }

      syncPrice() {
        const source = this.sourcePrice;
        if (!source || !this.priceTarget) return;
        if (this.priceTarget.innerHTML !== source.innerHTML) {
          this.priceTarget.innerHTML = source.innerHTML;
        }
        this.priceTarget.classList.toggle(
          "visibility-hidden",
          source.classList.contains("visibility-hidden"),
        );
      }

      /* ── Show / hide ── */

      render() {
        const shouldShow =
          this.available !== false && this.pastAnchor && !this.blocked;
        if (shouldShow === this.shown) return;
        this.shown = shouldShow;
        this.classList.toggle("sticky-atc--visible", shouldShow);
        this.toggleAttribute("hidden", !shouldShow);
        this.setAttribute("aria-hidden", shouldShow ? "false" : "true");
        this.updateWidgetAvoidance();
        this.toggleWidgetPolling(shouldShow);
      }

      /* ── Keep bottom-anchored app widgets (chat, quiz) clear of the bar ── */

      scheduleWidgetAvoidance() {
        if (this.avoidanceFrame) return;
        this.avoidanceFrame = requestAnimationFrame(() => {
          this.avoidanceFrame = null;
          this.updateWidgetAvoidance();
        });
      }

      // A widget that attaches a *closed-then-opened* shadow root to an
      // already-present host element (common for chat widgets that render
      // async, after fetching their own config) never mutates the light DOM —
      // attachShadow() itself isn't observable, and content added inside the
      // new root is a separate tree our body-level observer can't see. The
      // mutation observer catches everything else; this poll is the fallback
      // for that one case. Only runs while the bar is visible, and gives up
      // after 10s of finding nothing new — restarted fresh next time the bar
      // shows, e.g. scroll away and back.
      toggleWidgetPolling(shouldShow) {
        if (!shouldShow) {
          clearInterval(this.widgetPollId);
          this.widgetPollId = null;
          this.widgetPollMisses = 0;
          return;
        }
        if (this.widgetPollId) return;
        this.widgetPollMisses = 0;
        this.widgetPollId = setInterval(() => {
          const before = this.adjusted.size;
          this.updateWidgetAvoidance();
          this.widgetPollMisses =
            this.adjusted.size > before ? 0 : this.widgetPollMisses + 1;
          if (this.widgetPollMisses >= 20) {
            clearInterval(this.widgetPollId);
            this.widgetPollId = null;
          }
        }, 500);
      }

      // The bar is a card centred in the page, not a full-width slab, so on a
      // roomy desktop window the chat bubble and the signup teaser sit in the
      // gutters either side of it and are left exactly where they are — bar and
      // widgets all on one plane. Only widgets the card actually reaches get
      // touched: a compact launcher (the chat bubble) rides up above the bar,
      // while a wide card (the Klaviyo "match with a tent" teaser) would just
      // stack awkwardly on top of it, so it hides until the bar goes away. On
      // mobile the bar is full-bleed, so that always means bubble up, card gone.
      updateWidgetAvoidance() {
        if (!this.shown) {
          this.releaseWidgets();
          document.documentElement.style.removeProperty("--sticky-atc-height");
          return;
        }

        const offset = Math.round(this.getBoundingClientRect().height) + 8;
        if (offset <= 8) return;
        document.documentElement.style.setProperty(
          "--sticky-atc-height",
          `${offset}px`,
        );

        // The visible card, not the host: the host spans the full viewport
        // width and would report a clash with everything.
        const card = this.querySelector(".sticky-atc__inner") || this;
        const cardRect = this.restingRect(card);

        this.findBottomWidgets().forEach((el) => {
          // Already moved, so its rect no longer says where it rests. Decisions
          // are re-made from scratch on resize, which releases the map first.
          if (this.adjusted.has(el)) return;

          const rect = el.getBoundingClientRect();
          if (!this.overlapsCard(rect, cardRect)) return;

          if (this.isCardWidget(el, rect)) {
            this.hideWidget(el);
          } else {
            this.liftWidget(el, offset);
          }
        });
      }

      // Where the card sits once it has finished sliding in. This runs the
      // moment the bar is told to show, while it is still translated off the
      // bottom of the screen, so the raw rect would say it clashes with nothing
      // — back out the host's slide-in transform to get the settled position.
      restingRect(card) {
        const rect = card.getBoundingClientRect();
        const transform = getComputedStyle(this).transform;
        if (!transform || transform === "none" || !window.DOMMatrix) return rect;

        let matrix;
        try {
          matrix = new DOMMatrix(transform);
        } catch (e) {
          return rect;
        }

        return {
          left: rect.left - matrix.m41,
          right: rect.right - matrix.m41,
          top: rect.top - matrix.m42,
          bottom: rect.bottom - matrix.m42,
        };
      }

      releaseWidgets() {
        this.adjusted.forEach((snapshot, el) => this.restoreWidget(el, snapshot));
        this.adjusted.clear();
      }

      // A little breathing room, so a widget that merely comes close to the
      // card's edge is treated as clashing rather than left touching it.
      overlapsCard(rect, cardRect) {
        const gutter = 12;
        return (
          rect.right > cardRect.left - gutter &&
          rect.left < cardRect.right + gutter &&
          rect.bottom > cardRect.top - gutter &&
          rect.top < cardRect.bottom + gutter
        );
      }

      // Klaviyo names its own containers, which is the reliable signal for the
      // teaser we specifically want gone. The shape test covers the rest: a
      // launcher is a button (roughly square), anything markedly wider than it
      // is tall is a card or banner.
      isCardWidget(el, rect) {
        if (this.isKlaviyoWidget(el)) return true;
        return rect.width >= 200 && rect.width >= rect.height * 1.6;
      }

      isKlaviyoWidget(el) {
        const selector =
          '[class*="klaviyo"],[class*="kl-private"],[class*="kl-teaser"]';
        if (el.matches && el.matches(selector)) return true;
        if (el.closest && el.closest(selector)) return true;
        return !!(el.querySelector && el.querySelector(selector));
      }

      liftWidget(el, offset) {
        const computed = getComputedStyle(el).transform;
        const base = computed && computed !== "none" ? `${computed} ` : "";
        this.adjusted.set(el, this.captureStyles(el, ["transform"]));
        el.style.transition = "transform 0.25s ease";
        // !important, because some widgets set their own transform from a
        // stylesheet and would otherwise win over an inline style.
        el.style.setProperty(
          "transform",
          `${base}translateY(-${offset}px)`,
          "important",
        );
        el.classList.add("sticky-atc-lifted");
      }

      hideWidget(el) {
        this.adjusted.set(
          el,
          this.captureStyles(el, ["opacity", "visibility", "pointer-events"]),
        );
        el.style.transition = "opacity 0.2s ease, visibility 0.2s ease";
        el.style.setProperty("opacity", "0", "important");
        el.style.setProperty("visibility", "hidden", "important");
        el.style.setProperty("pointer-events", "none", "important");
        el.classList.add("sticky-atc-hidden");
      }

      captureStyles(el, properties) {
        const snapshot = { transition: el.style.transition, properties: {} };
        properties.forEach((property) => {
          snapshot.properties[property] = {
            value: el.style.getPropertyValue(property),
            priority: el.style.getPropertyPriority(property),
          };
        });
        return snapshot;
      }

      restoreWidget(el, snapshot) {
        Object.keys(snapshot.properties).forEach((property) => {
          const { value, priority } = snapshot.properties[property];
          el.style.removeProperty(property);
          if (value) el.style.setProperty(property, value, priority);
        });
        el.style.transition = snapshot.transition;
        el.classList.remove("sticky-atc-lifted", "sticky-atc-hidden");
      }

      findBottomWidgets() {
        const viewportHeight = window.innerHeight;
        const candidates = [];

        // Apps mount their widgets at arbitrary depths — sometimes several
        // wrappers down, sometimes inside a full-screen pointer-events:none
        // container, sometimes on <html> rather than <body>. So walk the whole
        // tree rather than assuming they're body children. Runs only when the
        // bar shows/hides or a node is added, never per scroll frame.
        const visit = (node) => {
          for (const el of node.children) {
            if (el === this) continue;
            if (/^(SCRIPT|STYLE|LINK|TEMPLATE|NOSCRIPT|HEAD)$/.test(el.tagName)) {
              continue;
            }

            const style = getComputedStyle(el);
            if (style.display === "none") continue;

            // The bar's own ancestors (<body>) must not be lifted, but we still
            // have to walk through them to reach anything.
            if (
              !el.contains(this) &&
              style.position === "fixed" &&
              style.visibility !== "hidden" &&
              this.isBottomWidget(el, viewportHeight)
            ) {
              candidates.push(el);
            }

            // Keep descending regardless: a qualifying widget is often nested
            // inside a fixed full-screen container that is itself disqualified.
            visit(el);
            // Redo's chat widget lives in a shadow root, which element.children
            // does not cross. Open roots only — a closed one is unreachable.
            if (el.shadowRoot) visit(el.shadowRoot);
          }
        };
        visit(document.documentElement);

        // When both a wrapper and something inside it qualify, move the inner
        // one — that's the widget, the outer is its positioning shell.
        return candidates.filter(
          (el) =>
            !candidates.some(
              (other) => other !== el && this.containsDeep(el, other),
            ),
        );
      }

      // Element.contains() stops at shadow boundaries; this walks through them.
      containsDeep(ancestor, node) {
        let current = node;
        while (current) {
          if (current === ancestor) return true;
          current = current.parentNode || current.host;
        }
        return false;
      }

      isBottomWidget(el, viewportHeight) {
        const rect = el.getBoundingClientRect();
        if (!rect.width || !rect.height) return false;
        // Full-screen overlays (modals, drawers, backdrops) aren't widgets and
        // must not be shunted around.
        if (rect.height > viewportHeight * 0.5) return false;
        // Only things resting near the bottom edge of the viewport.
        if (rect.bottom < viewportHeight - 100) return false;
        if (rect.top > viewportHeight) return false;
        return true;
      }

      /* ── Proxy the click to the real add-to-cart button ── */

      onClick(event) {
        event.preventDefault();
        const source = this.sourceButton;
        if (!source) return;
        if (
          source.hasAttribute("disabled") ||
          source.getAttribute("aria-disabled") === "true"
        ) {
          return;
        }
        source.click();
      }
    },
  );
}
